import React, { useCallback, useState } from "react";

import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  PaginationItem,
  Popover,
  Stack,
  Typography,
} from "@mui/material";

import { ArrowDownIcon, TickCircle } from "../../Icons/Icons";

export default function PaginationControls({
  totalItems,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions,
  loading,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const DEFAULT_PAGE_SIZE = 10;
  const DEFAULT_PAGE = 1;

  const currentPage = paginationModel?.page || DEFAULT_PAGE;
  const currentSize = paginationModel?.pageSize || pageSizeOptions[0] || DEFAULT_PAGE_SIZE;
  const safePageSize = pageSizeOptions.includes(currentSize) ? currentSize : pageSizeOptions[0] || DEFAULT_PAGE_SIZE;
  const totalPages = Math.ceil(totalItems / safePageSize);

  const startItem = (currentPage - 1) * safePageSize + 1;
  const endItem = Math.min(currentPage * safePageSize, totalItems);

  const handleSizeClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleSizeClose = useCallback(() => setAnchorEl(null), []);
  const handleSizeSelect = useCallback(
    (size) => {
      onPaginationModelChange({ page: 1, pageSize: size });
      handleSizeClose();
    },
    [onPaginationModelChange, handleSizeClose]
  );

  if (loading || totalItems <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        background: "background.paper",
        padding: "15px 0px",
        borderTop: "1px solid var(--mui-palette-grey-500)",
        borderRadius: "0px 0px 8px 8px",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={3}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(_, page) => onPaginationModelChange({ ...paginationModel, page })}
          showFirstButton
          showLastButton
          siblingCount={0}
          boundaryCount={0}
          shape="rounded"
          color="primary"
          renderItem={(item) => {
            if (["start-ellipsis", "end-ellipsis"].includes(item.type)) return null;
            if (item.type === "page" && item.page !== currentPage) return null;
            return <PaginationItem {...item} />;
          }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <Button
          size="small"
          onClick={handleSizeClick}
          sx={{
            textTransform: "none",
            minWidth: 80,
            maxHeight: 35,
            backgroundColor: "background.paper",
            color: "text.primary",
            border: "1px solid var(--mui-palette-grey-200)",
            borderRadius: "10px",
            fontSize: "18px",
            fontWeight: "600",
            "&:hover": {
              backgroundColor: "background.paper",
              borderColor: "var(--mui-palette-grey-200)",
            },
            "&:focus": {
              outline: "none",
              backgroundColor: "background.paper",
              borderColor: "var(--mui-palette-grey-200)",
            },
            "&:active": {
              backgroundColor: "background.paper",
              borderColor: "var(--mui-palette-grey-200)",
            },
          }}
        >
          {`${startItem}-${endItem}`} <ArrowDownIcon sx={{ fontSize: "18px", ml: 1 }} />
        </Button>
        <Typography sx={{ fontWeight: "600", fontSize: "18px" }}>{`of ${totalItems}`}</Typography>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleSizeClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 300,
                overflowY: "auto",
                padding: 0,
                border: "1px solid var(--mui-palette-grey-500)",
                borderRadius: "14px",
              },
            },
          }}
        >
          <List dense>
            {pageSizeOptions.map((n, index) => (
              <React.Fragment key={n}>
                <ListItemButton
                  onClick={() => handleSizeSelect(n)}
                  selected={paginationModel.pageSize === n}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <ListItemText primary={`${n} per page`} />
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      justifyContent: "flex-end",
                      marginTop: "5px",
                    }}
                  >
                    {safePageSize === n && (
                      <TickCircle
                        fontSize="small"
                        sx={{
                          color: "var(--mui-palette-primary-dark)",
                          "[data-mui-color-scheme='dark'] &": { color: "var(--mui-palette-primary-light)" },
                        }}
                      />
                    )}
                  </ListItemIcon>
                </ListItemButton>
                {index < pageSizeOptions.length - 1 && <Divider sx={{ margin: "0px 10px !important" }} />}
              </React.Fragment>
            ))}
          </List>
        </Popover>
      </Stack>
    </Box>
  );
}
